import { useState, useEffect, useMemo, useCallback } from 'react';
import { sheetsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    DollarSign,
    Clock,
    Phone,
    TrendingUp,
    Banknote,
    Smartphone,
    ClipboardList
} from 'lucide-react';
import {
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ComposedChart,
    Line,
    Legend,
    CartesianGrid
} from 'recharts';
import { useDashboardStatus } from '../hooks/useDashboardStatus';
import SetupInProgressBanner from '../components/SetupInProgressBanner';

export default function AnalyticsPage() {
    const { user } = useAuth();
    const { ready: dashboardReady } = useDashboardStatus();
    const [calls, setCalls] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Default price for jobs without a quoted price
    const DEFAULT_PRICE = 175;
    
    // Helper to format date for input
    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Default date range: yesterday to today
    const getDefaultDates = () => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
            start: formatDateForInput(yesterday),
            end: formatDateForInput(today)
        };
    };

    // Filters - default to yesterday and today
    const defaultDates = getDefaultDates();
    const [startDate, setStartDate] = useState(defaultDates.start);
    const [endDate, setEndDate] = useState(defaultDates.end);

    // Helper function to parse timestamp
    const parseTimestamp = (timestamp) => {
        if (!timestamp) return null;
        try {
            const parsed = new Date(timestamp);
            if (!isNaN(parsed.getTime())) return parsed;
            
            const match = timestamp.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
            if (match) {
                let [, month, day, year, hours, minutes, ampm] = match;
                hours = parseInt(hours);
                if (ampm) {
                    if (ampm.toUpperCase() === 'PM' && hours !== 12) hours += 12;
                    if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
                }
                return new Date(year, month - 1, day, hours, parseInt(minutes));
            }
            return null;
        } catch {
            return null;
        }
    };

    // Format date for display (MM/DD/YYYY)
    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    };

    const fetchCalls = useCallback(async () => {
        if (!user?.company_id) {
            setLoading(false);
            return;
        }

        try {
            // Fetch both call logs and master job log in parallel
            const [callsResponse, jobsResponse] = await Promise.all([
                sheetsAPI.getCallLogsFromSheet(user.company_id),
                sheetsAPI.getMasterJobLog(user.company_id)
            ]);
            
            const callsData = callsResponse.data.calls || [];
            const jobsData = jobsResponse.data.jobs || [];
            
            setCalls(callsData);
            setJobs(jobsData);
            
            // Calculate date range from data
            if (callsData.length > 0) {
                let minDate = null;
                let maxDate = null;
                
                callsData.forEach(call => {
                    const callDate = parseTimestamp(call.timestamp);
                    if (callDate) {
                        if (!minDate || callDate < minDate) minDate = callDate;
                        if (!maxDate || callDate > maxDate) maxDate = callDate;
                    }
                });
                
                // Don't override the default dates - user can expand if needed
            }
        } catch (err) {
            console.error('Error fetching analytics data:', err);
            toast.error('Failed to load analytics data. Please refresh page');
        } finally {
            setLoading(false);
        }
    }, [user?.company_id]);

    useEffect(() => {
        fetchCalls();
    }, [fetchCalls]);

    // Filter calls by date range
    const filteredCalls = useMemo(() => {
        return calls.filter(call => {
            if (startDate && call.timestamp) {
                const callDate = parseTimestamp(call.timestamp);
                if (!callDate) return true;
                // Parse start date components to avoid timezone issues
                const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
                const filterStartDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
                if (callDate < filterStartDate) return false;
            }
            if (endDate && call.timestamp) {
                const callDate = parseTimestamp(call.timestamp);
                if (!callDate) return true;
                // Parse end date components to avoid timezone issues
                const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
                const filterEndDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
                if (callDate > filterEndDate) return false;
            }
            return true;
        });
    }, [calls, startDate, endDate]);

    // Filter jobs by date range (using timestamp_booked)
    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            // Only include non-cancelled jobs
            if (job.cancelled) return false;
            
            // Must have a valid timestamp_booked to be included in date-filtered results
            if (!job.timestamp_booked) return false;
            
            const jobDate = parseTimestamp(job.timestamp_booked);
            if (!jobDate) return false;
            
            if (startDate) {
                const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
                const filterStartDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
                if (jobDate < filterStartDate) return false;
            }
            if (endDate) {
                const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
                const filterEndDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
                if (jobDate > filterEndDate) return false;
            }
            return true;
        });
    }, [jobs, startDate, endDate]);

    // Calculate revenue analytics
    const revenueStats = useMemo(() => {
        let jobsWithPrice = 0;
        let jobsWithoutPrice = 0;
        let actualRevenue = 0;
        let estimatedRevenue = 0;

        filteredJobs.forEach(job => {
            if (job.price && job.price > 0) {
                jobsWithPrice++;
                actualRevenue += job.price;
            } else {
                jobsWithoutPrice++;
                estimatedRevenue += DEFAULT_PRICE;
            }
        });

        return {
            total: actualRevenue + estimatedRevenue,
            actual: actualRevenue,
            estimated: estimatedRevenue,
            jobsWithPrice,
            jobsWithoutPrice,
            totalJobs: jobsWithPrice + jobsWithoutPrice,
            hasEstimates: jobsWithoutPrice > 0
        };
    }, [filteredJobs, DEFAULT_PRICE]);

    // Calculate analytics
    const analytics = useMemo(() => {
        const totalCalls = filteredCalls.length;
        const totalCost = filteredCalls.reduce((sum, c) => sum + (c.cost || 0), 0);
        const totalSeconds = filteredCalls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0);
        const totalMinutes = totalSeconds / 60;
        const avgCostPerCall = totalCalls > 0 ? totalCost / totalCalls : 0;

        return {
            totalCost,
            totalMinutes,
            totalCalls,
            avgCostPerCall
        };
    }, [filteredCalls]);

    // Calculate daily data for charts
    const dailyData = useMemo(() => {
        const dailyMap = {};
        
        filteredCalls.forEach(call => {
            const callDate = parseTimestamp(call.timestamp);
            if (!callDate) return;
            
            const dateKey = `${callDate.getMonth() + 1}/${callDate.getDate()}/${callDate.getFullYear()}`;
            
            if (!dailyMap[dateKey]) {
                dailyMap[dateKey] = {
                    date: dateKey,
                    dateObj: new Date(callDate.getFullYear(), callDate.getMonth(), callDate.getDate()),
                    calls: 0,
                    cost: 0,
                    minutes: 0
                };
            }
            
            dailyMap[dateKey].calls += 1;
            dailyMap[dateKey].cost += (call.cost || 0);
            dailyMap[dateKey].minutes += (call.duration_seconds || 0) / 60;
        });

        return Object.values(dailyMap)
            .sort((a, b) => a.dateObj - b.dateObj)
            .map(d => ({
                date: d.date,
                calls: d.calls,
                cost: parseFloat(d.cost.toFixed(2)),
                minutes: parseFloat(d.minutes.toFixed(1))
            }));
    }, [filteredCalls]);

    // Calculate daily revenue data for chart
    const dailyRevenueData = useMemo(() => {
        const dailyMap = {};
        
        filteredJobs.forEach(job => {
            const jobDate = parseTimestamp(job.timestamp_booked);
            if (!jobDate) return;
            
            const dateKey = `${jobDate.getMonth() + 1}/${jobDate.getDate()}/${jobDate.getFullYear()}`;
            
            if (!dailyMap[dateKey]) {
                dailyMap[dateKey] = {
                    date: dateKey,
                    dateObj: new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate()),
                    actualRevenue: 0,
                    estimatedRevenue: 0,
                    totalRevenue: 0,
                    jobs: 0
                };
            }
            
            dailyMap[dateKey].jobs += 1;
            if (job.price && job.price > 0) {
                dailyMap[dateKey].actualRevenue += job.price;
                dailyMap[dateKey].totalRevenue += job.price;
            } else {
                dailyMap[dateKey].estimatedRevenue += DEFAULT_PRICE;
                dailyMap[dateKey].totalRevenue += DEFAULT_PRICE;
            }
        });

        return Object.values(dailyMap)
            .sort((a, b) => a.dateObj - b.dateObj)
            .map(d => ({
                date: d.date,
                actualRevenue: parseFloat(d.actualRevenue.toFixed(2)),
                estimatedRevenue: parseFloat(d.estimatedRevenue.toFixed(2)),
                totalRevenue: parseFloat(d.totalRevenue.toFixed(2)),
                jobs: d.jobs
            }));
    }, [filteredJobs, DEFAULT_PRICE]);

    const clearFilters = () => {
        const defaults = getDefaultDates();
        setStartDate(defaults.start);
        setEndDate(defaults.end);
    };

    // ── Booking-channel breakdown (Voice AI vs Online Form vs App) ───────────
    // Voice AI = `submission_type` null/missing (nothing in Column AD).
    // Online Form = "web_form". App = "app".
    // Cancelled jobs are excluded upstream in `filteredJobs`, keeping parity
    // with the Revenue Captured card.
    const channelStats = useMemo(() => {
        const base = (label, key, color, Icon) => ({
            label, key, color, Icon,
            count: 0,
            actualRevenue: 0,
            estimatedRevenue: 0,
            get totalRevenue() { return this.actualRevenue + this.estimatedRevenue; },
        });
        const buckets = {
            voice_ai:   base('Voice AI',    'voice_ai',   '#F97316', Phone),
            web_form:   base('Online Form', 'web_form',   '#F59E0B', ClipboardList),
            app:        base('App',         'app',        '#3B82F6', Smartphone),
        };

        filteredJobs.forEach(job => {
            const type = job.submission_type === 'app'
                ? 'app'
                : job.submission_type === 'web_form'
                    ? 'web_form'
                    : 'voice_ai';
            const b = buckets[type];
            b.count += 1;
            if (job.price && job.price > 0) {
                b.actualRevenue += job.price;
            } else {
                b.estimatedRevenue += DEFAULT_PRICE;
            }
        });

        const totalCount = buckets.voice_ai.count + buckets.web_form.count + buckets.app.count;
        const withPct = (b) => ({
            ...b,
            totalRevenue: b.actualRevenue + b.estimatedRevenue,
            pct: totalCount > 0 ? (b.count / totalCount) * 100 : 0,
        });
        return {
            totalCount,
            voice_ai: withPct(buckets.voice_ai),
            web_form: withPct(buckets.web_form),
            app: withPct(buckets.app),
            // visibleTiles: always show Voice AI; hide the others only if they
            // have zero bookings in the selected date range.
            get visibleTiles() {
                const tiles = [this.voice_ai];
                if (this.web_form.count > 0) tiles.push(this.web_form);
                if (this.app.count > 0) tiles.push(this.app);
                return tiles;
            },
        };
    }, [filteredJobs, DEFAULT_PRICE]);

    // Trend data: bookings per day, grouped by channel.
    const dailyChannelData = useMemo(() => {
        const dailyMap = {};
        filteredJobs.forEach(job => {
            const jobDate = parseTimestamp(job.timestamp_booked);
            if (!jobDate) return;
            const dateKey = `${jobDate.getMonth() + 1}/${jobDate.getDate()}/${jobDate.getFullYear()}`;
            if (!dailyMap[dateKey]) {
                dailyMap[dateKey] = {
                    date: dateKey,
                    dateObj: new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate()),
                    voice_ai: 0, web_form: 0, app: 0,
                };
            }
            const type = job.submission_type === 'app'
                ? 'app'
                : job.submission_type === 'web_form'
                    ? 'web_form'
                    : 'voice_ai';
            dailyMap[dateKey][type] += 1;
        });
        return Object.values(dailyMap)
            .sort((a, b) => a.dateObj - b.dateObj)
            .map(({ dateObj, ...rest }) => rest);
    }, [filteredJobs]);
    // ─────────────────────────────────────────────────────────────────────────

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Custom tooltip for charts
    const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-2 border rounded shadow-sm">
                    <p className="text-sm font-medium">{label}</p>
                    <p className="text-sm text-brand-orange-500">
                        {valuePrefix}{payload[0].value.toLocaleString()}{valueSuffix}
                    </p>
                </div>
            );
        }
        return null;
    };

    // Custom tooltip for revenue chart with breakdown
    const RevenueTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-3 border rounded shadow-sm">
                    <p className="text-sm font-medium mb-2">{label}</p>
                    <p className="text-sm text-green-600">
                        Total: {formatCurrency(data.totalRevenue)}
                    </p>
                    {data.estimatedRevenue > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                            (Includes {formatCurrency(data.estimatedRevenue)} estimated*)
                        </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                        {data.jobs} job{data.jobs !== 1 ? 's' : ''} booked
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
            {!dashboardReady && <SetupInProgressBanner />}
            {/* Date Range Filter */}
            <Card className="mb-6">
                <CardContent className="p-6">
                    <h3 className="text-lg font-heading font-bold text-center mb-6">Date Range Filter</h3>
                    <div className="flex flex-col sm:flex-row items-end justify-center gap-4">
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">From Date</label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-48"
                                data-testid="analytics-start-date"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">To Date</label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-48"
                                data-testid="analytics-end-date"
                            />
                        </div>
                        <Button 
                            variant="outline" 
                            onClick={clearFilters}
                            data-testid="analytics-clear-btn"
                        >
                            Clear
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-6">
                                <Skeleton className="h-4 w-24 mb-2" />
                                <Skeleton className="h-10 w-32" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="analytics-stats">
                    {/* Total Cost */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Total Cost</p>
                                    <p className="text-3xl font-heading font-bold text-gray-900">
                                        ${analytics.totalCost.toFixed(4)}
                                    </p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Across {analytics.totalCalls} calls
                                    </p>
                                </div>
                                <DollarSign className="w-5 h-5 text-brand-orange-500" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Calling Minutes */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Calling Minutes</p>
                                    <p className="text-3xl font-heading font-bold text-gray-900">
                                        {analytics.totalMinutes.toFixed(1)}
                                    </p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Total minutes used
                                    </p>
                                </div>
                                <Clock className="w-5 h-5 text-brand-orange-500" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Number of Calls */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Number of Calls</p>
                                    <p className="text-3xl font-heading font-bold text-gray-900">
                                        {analytics.totalCalls}
                                    </p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Total calls made
                                    </p>
                                </div>
                                <Phone className="w-5 h-5 text-brand-orange-500" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Avg Cost per Call */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Avg Cost per Call</p>
                                    <p className="text-3xl font-heading font-bold text-gray-900">
                                        ${analytics.avgCostPerCall.toFixed(4)}
                                    </p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Average per call
                                    </p>
                                </div>
                                <TrendingUp className="w-5 h-5 text-brand-orange-500" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Revenue Captured by Towmatic Card */}
            {!loading && (
                <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-medium text-green-700">Revenue Captured by Towmatic</p>
                                    {revenueStats.hasEstimates && (
                                        <span className="text-green-600">*</span>
                                    )}
                                </div>
                                <p className="text-4xl font-heading font-bold text-green-700">
                                    {formatCurrency(revenueStats.total)}
                                </p>
                                <div className="mt-2 space-y-1">
                                    <p className="text-sm text-green-600">
                                        {revenueStats.totalJobs} job{revenueStats.totalJobs !== 1 ? 's' : ''} booked
                                    </p>
                                    {revenueStats.hasEstimates && (
                                        <p className="text-xs text-green-600/70">
                                            ({formatCurrency(revenueStats.actual)} quoted + {formatCurrency(revenueStats.estimated)} estimated)
                                        </p>
                                    )}
                                </div>
                                {revenueStats.hasEstimates && (
                                    <p className="text-xs text-green-600/60 mt-3 italic">
                                        *Some revenue is estimated based on average job values when quotes weren't provided
                                    </p>
                                )}
                            </div>
                            <div className="p-3 bg-green-100 rounded-xl">
                                <Banknote className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ── Booking Channels ────────────────────────────────────────── */}
            {!loading && channelStats.totalCount > 0 && (
                <Card className="mb-6 bg-white" data-testid="analytics-booking-channels">
                    <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-4 sm:mb-5">
                            <h3 className="text-lg font-heading font-bold">Booking Channels</h3>
                            <p className="text-xs sm:text-sm text-gray-500">
                                Where {channelStats.totalCount} booking{channelStats.totalCount !== 1 ? 's' : ''} came from
                            </p>
                        </div>

                        {/* Channel tiles — 1 col on mobile, auto-split on ≥sm */}
                        <div className={`grid gap-3 sm:gap-4 mb-5 sm:mb-6 grid-cols-1 ${
                            channelStats.visibleTiles.length === 1 ? '' :
                            channelStats.visibleTiles.length === 2 ? 'sm:grid-cols-2' :
                            'sm:grid-cols-3'
                        }`}>
                            {channelStats.visibleTiles.map(tile => {
                                const Icon = tile.Icon;
                                return (
                                    <div
                                        key={tile.key}
                                        data-testid={`analytics-channel-tile-${tile.key.replace('_', '-')}`}
                                        className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-2 hover:shadow-sm transition-shadow"
                                        style={{ borderLeft: `4px solid ${tile.color}` }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">
                                                {tile.label}
                                            </span>
                                            <div
                                                className="p-2 rounded-lg"
                                                style={{ backgroundColor: `${tile.color}1A` }}
                                            >
                                                <Icon className="w-4 h-4" style={{ color: tile.color }} />
                                            </div>
                                        </div>
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span className="text-2xl sm:text-3xl font-heading font-bold text-gray-900">
                                                {tile.count}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {tile.pct.toFixed(0)}% of bookings
                                            </span>
                                        </div>
                                        <p className="text-sm font-semibold" style={{ color: tile.color }}>
                                            {formatCurrency(tile.totalRevenue)} captured
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Comparison bar — single horizontal stacked bar */}
                        {channelStats.visibleTiles.length > 1 && (
                            <div data-testid="analytics-channel-bar" className="mb-5 sm:mb-6">
                                <div className="flex w-full h-3 rounded-full overflow-hidden bg-gray-100">
                                    {channelStats.visibleTiles.map(tile => (
                                        tile.pct > 0 && (
                                            <div
                                                key={tile.key}
                                                className="h-full first:rounded-l-full last:rounded-r-full transition-all"
                                                style={{ width: `${tile.pct}%`, backgroundColor: tile.color }}
                                                title={`${tile.label}: ${tile.pct.toFixed(1)}%`}
                                            />
                                        )
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                                    {channelStats.visibleTiles.map(tile => (
                                        <div key={tile.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                                            <div
                                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: tile.color }}
                                            />
                                            <span>{tile.label}</span>
                                            <span className="text-gray-400">·</span>
                                            <span className="font-medium text-gray-700">{tile.pct.toFixed(0)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Stacked daily bookings by channel */}
                        {dailyChannelData.length > 0 && channelStats.visibleTiles.length > 1 && (
                            <div data-testid="analytics-channel-trend-chart">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">
                                    Daily Bookings by Channel
                                </h4>
                                <div className="h-56 sm:h-64 -mx-2 sm:mx-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={dailyChannelData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 10 }}
                                                angle={-45}
                                                textAnchor="end"
                                                height={55}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11 }}
                                                allowDecimals={false}
                                            />
                                            <Tooltip
                                                content={({ active, payload, label }) => {
                                                    if (!active || !payload?.length) return null;
                                                    const total = payload.reduce((s, p) => s + (p.value || 0), 0);
                                                    return (
                                                        <div className="bg-white p-3 border rounded shadow-sm text-xs">
                                                            <p className="font-medium mb-2 text-sm">{label}</p>
                                                            {payload.slice().reverse().map(p => (
                                                                p.value > 0 && (
                                                                    <p key={p.dataKey} style={{ color: p.color }}>
                                                                        {p.name}: {p.value}
                                                                    </p>
                                                                )
                                                            ))}
                                                            <p className="mt-1 pt-1 border-t border-gray-100 text-gray-500">
                                                                Total: {total}
                                                            </p>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Legend
                                                wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                                                iconType="circle"
                                                iconSize={8}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="voice_ai"
                                                name="Voice AI"
                                                stackId="channels"
                                                stroke={channelStats.voice_ai.color}
                                                fill={channelStats.voice_ai.color}
                                                fillOpacity={0.7}
                                                hide={channelStats.voice_ai.count === 0}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="web_form"
                                                name="Online Form"
                                                stackId="channels"
                                                stroke={channelStats.web_form.color}
                                                fill={channelStats.web_form.color}
                                                fillOpacity={0.7}
                                                hide={channelStats.web_form.count === 0}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="app"
                                                name="App"
                                                stackId="channels"
                                                stroke={channelStats.app.color}
                                                fill={channelStats.app.color}
                                                fillOpacity={0.7}
                                                hide={channelStats.app.count === 0}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
            {/* ───────────────────────────────────────────────────────────── */}

            {/* Charts */}
            {!loading && dailyData.length > 0 && (
                <div className="space-y-6">
                    {/* Revenue Captured Chart */}
                    {dailyRevenueData.length > 0 && (
                        <Card className="bg-white">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-heading font-bold">Revenue Captured by Towmatic</h3>
                                    {revenueStats.hasEstimates && (
                                        <span className="text-xs text-gray-500 italic">*Includes estimated values</span>
                                    )}
                                </div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={dailyRevenueData}>
                                            <defs>
                                                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
                                                </linearGradient>
                                            </defs>
                                            <XAxis 
                                                dataKey="date" 
                                                tick={{ fontSize: 10 }}
                                                angle={-45}
                                                textAnchor="end"
                                                height={60}
                                            />
                                            <YAxis 
                                                tick={{ fontSize: 12 }}
                                                tickFormatter={(value) => `$${value}`}
                                            />
                                            <Tooltip content={<RevenueTooltip />} />
                                            <Area 
                                                type="monotone" 
                                                dataKey="totalRevenue" 
                                                stroke="#22c55e"
                                                strokeWidth={2}
                                                fill="url(#revenueGradient)"
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="totalRevenue"
                                                stroke="#22c55e"
                                                strokeWidth={2}
                                                dot={{ fill: '#fff', stroke: '#22c55e', strokeWidth: 2, r: 4 }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex justify-center mt-4">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        <span>Daily Revenue</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Daily Call Volume - Bar Chart */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <h3 className="text-lg font-heading font-bold mb-4">Daily Call Volume</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dailyData}>
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fontSize: 10 }}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip content={<CustomTooltip valueSuffix=" calls" />} />
                                        <Bar 
                                            dataKey="calls" 
                                            fill="#F97316" 
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Daily Cost Trend - Area Chart */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <h3 className="text-lg font-heading font-bold mb-4">Daily Cost Trend</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={dailyData}>
                                        <defs>
                                            <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#F97316" stopOpacity={0.05}/>
                                            </linearGradient>
                                        </defs>
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fontSize: 10 }}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip content={<CustomTooltip valuePrefix="$" />} />
                                        <Area 
                                            type="monotone" 
                                            dataKey="cost" 
                                            stroke="#F97316"
                                            strokeWidth={2}
                                            fill="url(#costGradient)"
                                            dot={{ fill: '#fff', stroke: '#F97316', strokeWidth: 2, r: 3 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Daily Minutes Usage - Area Chart */}
                    <Card className="bg-white">
                        <CardContent className="p-6">
                            <h3 className="text-lg font-heading font-bold mb-4">Daily Minutes Usage</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={dailyData}>
                                        <defs>
                                            <linearGradient id="minutesGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#F97316" stopOpacity={0.05}/>
                                            </linearGradient>
                                        </defs>
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fontSize: 10 }}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip content={<CustomTooltip valueSuffix=" min" />} />
                                        <Area 
                                            type="monotone" 
                                            dataKey="minutes" 
                                            stroke="#F97316"
                                            strokeWidth={2}
                                            fill="url(#minutesGradient)"
                                            dot={{ fill: '#fff', stroke: '#F97316', strokeWidth: 2, r: 3 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex justify-center mt-4">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <div className="w-3 h-3 rounded-full bg-brand-orange-500"></div>
                                    <span>Minutes</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Empty State */}
            {!loading && dailyData.length === 0 && (
                <Card>
                    <CardContent className="p-8 text-center text-gray-500">
                        <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No call data available for the selected date range</p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
